// src/components/DietAssessment/index.js

import React, { useContext, useState, Suspense, lazy, useEffect } from 'react';
import { Box, Typography, Fab, Menu, MenuItem } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { ProgressDisplay } from './components/ProgressDisplay';
import { CenteredLoader } from '../../App';
import AssessmentStepErrorBoundary from './components/AssessmentStepErrorBoundary';
import { AssessmentContext, AssessmentContextProvider } from '../../lib/contexts';
import { useTheme } from '@mui/material/styles';
import { AssessmentNavigation } from './components/AssessmentNavigation';
const DefaultStep = lazy(() => import('./components/DefaultStep'));

const stepComponentMapping = {
  custom_user_consent: lazy(() => import('./components/CustomConsent')),
  dob_check: lazy(() => import('./components/DOBCheck')),
  diet_screener: lazy(() => import('./components/Screener')),
  photo_selection: lazy(() => import('./components/PhotoSelection')),
  diet_results: lazy(() => import('./components/DietResults')),
  id_to_ideal_results: lazy(() => import('./components/DietResults')),
  ideal_goals: lazy(() => import('./components/Ideal')),
  diet_ideal: lazy(() => import('./components/PhotoSelection')),
  multi_radio_select: lazy(() => import('./components/QuestionBoxAbstract')),
  question_select: lazy(() => import('./components/QuestionBoxAbstract')),
  question_select_dropdown: lazy(() => import('./components/QuestionBoxAbstract')),
  question_multiple_select: lazy(() => import('./components/QuestionBoxAbstract')),
  question_usps_address: lazy(() => import('./components/QuestionUSPSAddress')),
  question_input: lazy(() => import('./components/QuestionBoxAbstract')),
  user_info: lazy(() => import('./components/UserInfo')),
  question_email: lazy(() => import('./../EmailField')),
  question_phone_number: lazy(() => import('./../PhoneField')),
  embedded_code: lazy(() => import('./components/QuestionEmbeddedCode')),
};

const stepPropMapping = {
  diet_results: { variant: 'diet' },
  id_to_ideal_results: { variant: 'transition' },
  question_select: { variant: 'single' },
  question_multiple_select: { variant: 'multiple' },
  multi_radio_select: { variant: 'single' },
  question_input: { variant: 'input' },
  question_select_dropdown: { variant: 'dropdown' },
};

const DietAssessment = () => {
  const { step: last_current_step } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const { state, actions } = useContext(AssessmentContext);
  const { currentStepInfo, stepOverride, responses, progress, loadingNext, stepError, isLoading, error } = state;
  const { setCurrentStepChoices, setStepOverride, handleNextStep } = actions;

  const [anchorEl, setAnchorEl] = useState(null);
  const effectiveStep = stepOverride || (currentStepInfo && currentStepInfo.step) || last_current_step;

  const StepComponent = stepComponentMapping[currentStepInfo?.type] || DefaultStep;
  const extraProps = stepPropMapping[currentStepInfo?.type] || {};

  if (isLoading) return <CenteredLoader />;
  if (error) return <Typography color="error">{error.message}</Typography>;

  let subHeadingMessage = '';
  if (currentStepInfo?.sub_heading) {
    subHeadingMessage = currentStepInfo.sub_heading;
  } else {
    if (currentStepInfo?.options?.length > 1) {
      if (currentStepInfo?.type === 'question_multiple_select') {
        subHeadingMessage = 'Select all that apply.';
      } else {
        subHeadingMessage = 'Please select one.';
      }
    }
  }

  /**
   * Determine if parent assessment navigation should be displayed.
   * For Pseudo/Multi-step processes, the AssessmentNavgigation is not displayed
   * as it is included in the step component.
   *
   * diet_screener, user_info, ideal_goals are pseudo steps
   *
   * @param {String} step
   * @returns boolean if AssessmentNavigation should be displayed
   */
  const displayAssessmentNavigation = (step) => {
    if (step === 'user_info' || step === 'ideal_goals' || step === 'diet_screener') {
      return false;
    }
    return true;
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
      {progress && progress.length > 0 && <ProgressDisplay progress={progress} />}
      {loadingNext && <CenteredLoader />}
      {stepError && <Typography color="error">{stepError.message}</Typography>}

      <AssessmentStepErrorBoundary onGoBack={() => navigate(-1)}>
        <Suspense fallback={<CenteredLoader />}>
          {currentStepInfo && (
            <Box
              id="general-step-container"
              sx={theme.globalClasses.generalStepContainer}
            >
              <Box
                id="question-box-container"
                sx={theme.globalClasses.questionBoxContainer}
              >
                {(currentStepInfo?.heading || subHeadingMessage) && (
                  <Box
                    id="question-box-header-container"
                    sx={theme.globalClasses.questionBoxHeaderContainer}
                  >
                    {currentStepInfo?.heading &&
                      (currentStepInfo?.type === 'embedded_code' ? (
                        <div
                          id="embedded-code-field-title"
                          className="heading"
                          dangerouslySetInnerHTML={{ __html: currentStepInfo.heading }}
                          style={{ marginBottom: theme.spacing(2) }}
                        />
                      ) : (
                        <Typography
                          component="h2"
                          variant="assessmentSectionTitle"
                          id={`${currentStepInfo?.type ?? 'current-step'}-field-title`}
                          sx={{ mb: 2 }}
                        >
                          {currentStepInfo?.heading}
                        </Typography>
                      ))}
                    {currentStepInfo?.sub_heading && (
                      <Typography
                        component="p"
                        variant="assessmentSubtitle1"
                        sx={{ mb: 3 }}
                        id="assessment-sub-heading"
                      >
                        {subHeadingMessage}
                      </Typography>
                    )}
                  </Box>
                )}
                {/* <Typography
                    id="slug-text"
                    sx={theme.globalClasses.descriptionText}
                  >
                    {currentStepInfo?.fields?.[0]?.label}
                  </Typography> */}
                <StepComponent
                  stepInfo={{ ...currentStepInfo, step: effectiveStep }}
                  onChange={setCurrentStepChoices}
                  responses={responses}
                  {...currentStepInfo}
                  {...extraProps}
                />
                {displayAssessmentNavigation(currentStepInfo?.step) ? <AssessmentNavigation /> : null}
              </Box>
            </Box>
          )}
        </Suspense>
      </AssessmentStepErrorBoundary>
    </Box>
  );
};

const WrappedDietAssessment = () => (
  <AssessmentContextProvider>
    <DietAssessment />
  </AssessmentContextProvider>
);

export default WrappedDietAssessment;
